/**
 * src/sagas/user.ts
 *
 * Redux-Saga effects for user flows.
 *
 * All external API calls now go through backend routes (/api/*).
 * No API keys or secrets are used here — the frontend never touches them.
 *
 * DEPRECATED and removed (see DEPRECATIONS.md):
 *   - Direct Chatbase calls with REACT_APP_CHATBASE_API_KEY
 *   - Direct OpenAI calls with REACT_APP_OPENAI_API_KEY
 *   - Direct ElevenLabs calls with REACT_APP_ELEVENLABS_API_KEY
 *   - exportIkigai saga (OpenAI function-calling for field parsing) — deferred to Phase 2
 *   - speak / speakPlay / speakPlayPause sagas — deferred to Phase 2
 */

import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { ActionTypes } from 'literals';

import {
  loginFailure,
  loginSuccess,
  logOutSuccess,
  setSession,
  userAnswerFailure,
  userAnswerSuccess,
  userExportFailure,
} from 'actions';

// ── Login ─────────────────────────────────────────────────────────────────────
// /api/start-session is now a thin endpoint that just mints a sessionId — it
// does not touch Airtable, so it returns in <100ms regardless of base size.
// Airtable persistence is fire-and-forget via /api/persist-session below.
// On success, stashes sessionId in Redux and transitions to /chat.
// On failure, dispatches loginFailure with a user-visible message.

export function* loginSaga(): Generator<any, void, any> {
  try {
    const name: string = yield select(({ user }) => user.name);
    const email: string = yield select(({ user }) => user.email);

    let response: Response;
    try {
      response = yield call(fetch, '/api/start-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: name, email }),
      });
    } catch (networkErr) {
      console.error('[loginSaga] network error contacting /api/start-session:', networkErr);
      yield put(loginFailure('Network error — please check your connection and try again.'));
      return;
    }

    if (!response.ok) {
      let serverMessage = '';
      try {
        const data = yield call([response, response.json]);
        serverMessage = data?.error || '';
      } catch {
        // Non-JSON body (e.g. Vercel error pages).
      }
      console.error(`[loginSaga] start-session HTTP ${response.status}:`, serverMessage);
      const friendly =
        response.status === 504
          ? 'Our server is taking too long to start your session. Please try again in a moment.'
          : serverMessage || `Couldn't start your session (error ${response.status}). Please try again.`;
      yield put(loginFailure(friendly));
      return;
    }

    const data: {
      success: boolean;
      sessionId?: string;
      error?: string;
    } = yield call([response, response.json]);

    if (!data.success || !data.sessionId) {
      console.error('[loginSaga] start-session failed:', data.error);
      yield put(
        loginFailure(data.error || "Couldn't start your session. Please try again."),
      );
      return;
    }

    // interviewRecordId is no longer minted here — downstream endpoints
    // upsert by sessionId. Stash an empty string for backward compat with
    // the existing setSession signature.
    yield put(setSession(data.sessionId, ''));
    yield put(loginSuccess());

    // Fire-and-forget: persist User + Chatbase_Interview rows in the background.
    // The chat experience does not depend on this completing. If it fails or is
    // slow, /api/chat and /api/submit-session lazily upsert the interview record
    // by sessionId on their own writes.
    fetch('/api/persist-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: data.sessionId, firstName: name, email }),
    }).catch(err => {
      console.warn('[loginSaga] persist-session fire-and-forget failed:', err);
    });
  } catch (err: any) {
    console.error('[loginSaga] Unexpected error:', err);
    yield put(loginFailure(err?.message || 'Something went wrong. Please try again.'));
  }
}

// ── Logout ────────────────────────────────────────────────────────────────────

export function* logoutSaga() {
  yield put(logOutSuccess());
}

// ── Chat answer ───────────────────────────────────────────────────────────────
// Sends the full message history to /api/chat, which relays to Chatbase and
// writes the transcript to Airtable. Returns the bot's reply as a single string.
//
// NOTE: The original implementation used Chatbase streaming (incremental chunks).
// This version buffers the full reply server-side for architectural safety
// (secrets never touch the browser). The typing indicator still shows while
// the request is in flight. Streaming can be re-enabled later by implementing
// a streaming response in api/chat.ts and updating this saga to read chunks.

export function* userAnswerSaga(): Generator<any, void, any> {
  try {
    const coach: number = yield select(({ user }) => user.coach);
    const sessionId: string = yield select(({ user }) => user.sessionId);
    const firstName: string = yield select(({ user }) => user.name);
    const email: string = yield select(({ user }) => user.email);
    const messages: Array<{ role: string; content: string }> = yield select(
      ({ user }) => user.messages,
    );

    const response: Response = yield call(fetch, '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        firstName,
        email,
        messages: messages[coach],
      }),
    });

    const data: { success: boolean; reply?: string; error?: string } = yield call(
      [response, response.json],
    );

    if (!data.success || !data.reply) {
      console.error('[userAnswerSaga] chat failed:', data.error);
      yield put(userAnswerFailure());
      return;
    }

    // userAnswerSuccess(text, stream=false) replaces the current question in Redux.
    // The reducer sets questions[coach] = text.
    yield put(userAnswerSuccess(data.reply, false));
  } catch (err) {
    console.error('[userAnswerSaga] Unexpected error:', err);
    yield put(userAnswerFailure());
  }
}

// ── Export / field parsing — DEFERRED ────────────────────────────────────────
// OpenAI function-calling to extract structured ikigai fields is postponed.
// The transcript is stored in Airtable and can be parsed later via Make.
// This stub dispatches failure immediately so any UI waiting on it recovers.

export function* exportIkigai(): Generator<any, void, any> {
  console.log('[exportIkigai] Field parsing is deferred to Phase 2 (Make automation).');
  yield put(userExportFailure());
}

// ── Root saga ─────────────────────────────────────────────────────────────────

export default function* root() {
  yield all([
    // takeLeading — ignore Begin Chat clicks while a previous attempt is in
    // flight, so mashing the button never creates duplicate Airtable rows.
    takeLeading(ActionTypes.USER_LOGIN_REQUEST, loginSaga),
    takeLatest(ActionTypes.USER_LOGOUT_REQUEST, logoutSaga),
    takeLatest(ActionTypes.USER_ANSWER_REQUEST, userAnswerSaga),
    takeLatest(ActionTypes.USER_EXPORT_REQUEST, exportIkigai),
    // USER_SPEAK_REQUEST — ElevenLabs TTS deferred to Phase 2
  ]);
}
